/**
 * b15b U6 — persistent, recallable Calendar to-do list.
 *
 * The Calendar's "Your plan" list previously read the in-memory
 * `plannedEvents` Set from SessionProvider, so it evaporated on reload and was
 * not recallable when the trainer came back later. This store persists the
 * plan to localStorage, keyed by event id + the event's date, so the same plan
 * is visible from any Calendar sub-screen and survives an app restart.
 *
 * Semantics (per the U6 brief):
 *   - Items are keyed by `${eventId}@${date}` so the SAME event on two
 *     different dates (e.g. a recurring Community Day) are distinct to-dos.
 *   - Ticking an item OFF removes it from the list entirely — a done item is
 *     not kept around greyed-out; the list is a live "what's left" plan.
 *
 * Pure + DOM-event driven, mirroring scanHistoryStore: every mutation writes
 * the blob and dispatches CALENDAR_TODO_EVENT so any mounted Calendar surface
 * re-reads immediately.
 */
import { isEventOver, eventStartMs } from "./eventRecency";

const CALENDAR_TODO_KEY = "gx.calendarTodo.v1";
export const CALENDAR_TODO_EVENT = "gx:calendar-todo-updated";

export interface CalendarTodoItem {
  /** Stable composite key: `${eventId}@${date}`. */
  id: string;
  eventId: string;
  /** Human date label as shown on the event card (e.g. "Sat, May 16"). */
  date: string;
  title: string;
  /** ms epoch the item was added, so the list can sort oldest-first. */
  addedAt: number;
  /**
   * Event START/END as floating wall-clock ISO strings (same shape the event
   * feed uses). Stored so the list can auto-drop an event once its END time is
   * past and sort ascending by start — evaluated via the shared eventRecency
   * helper so Calendar and Raid Radar agree. Optional for backward-compat with
   * items saved before this field existed.
   */
  start?: string;
  end?: string;
}

interface CalendarTodoShape {
  items: CalendarTodoItem[];
}

function keyFor(eventId: string, date: string): string {
  return `${eventId}@${date}`;
}

function normalizeItem(raw: unknown): CalendarTodoItem | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Partial<CalendarTodoItem> & Record<string, unknown>;
  if (typeof e.eventId !== "string" || e.eventId.length === 0) return null;
  const date = typeof e.date === "string" ? e.date : "";
  const id = typeof e.id === "string" && e.id.length > 0 ? e.id : keyFor(e.eventId, date);
  const item: CalendarTodoItem = {
    id,
    eventId: e.eventId,
    date,
    title: typeof e.title === "string" ? e.title : e.eventId,
    addedAt: typeof e.addedAt === "number" ? e.addedAt : Date.now(),
  };
  if (typeof e.start === "string" && e.start.length > 0) item.start = e.start;
  if (typeof e.end === "string" && e.end.length > 0) item.end = e.end;
  return item;
}

function readRaw(): CalendarTodoShape {
  try {
    if (typeof window === "undefined") return { items: [] };
    const raw = window.localStorage.getItem(CALENDAR_TODO_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    const items: CalendarTodoItem[] = [];
    for (const item of parsed.items) {
      const norm = normalizeItem(item);
      if (norm) items.push(norm);
    }
    return { items };
  } catch {
    return { items: [] };
  }
}

function writeRaw(shape: CalendarTodoShape): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CALENDAR_TODO_KEY, JSON.stringify(shape));
  } catch {
    /* quota exceeded — silently drop. */
  }
}

function emit(): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(CALENDAR_TODO_EVENT));
  } catch {
    /* SSR or non-DOM runtime — silent. */
  }
}

/**
 * Snapshot of the current plan, sorted ascending by event start (soonest
 * first). Events whose END time has passed are auto-dropped AND pruned from
 * the persisted store so they can never linger. Items with no stored start/end
 * (saved before that field existed) are kept and sorted last, since we cannot
 * prove they are over.
 *
 * Pruning writes back silently (no CALENDAR_TODO_EVENT) because this runs
 * during render; emitting here would re-enter the subscribers mid-render.
 */
export function listCalendarTodos(now: number = Date.now()): CalendarTodoItem[] {
  const shape = readRaw();
  const kept = shape.items.filter((i) => !isEventOver(i, now));
  if (kept.length !== shape.items.length) {
    writeRaw({ items: kept });
  }
  return kept.slice().sort((a, b) => {
    const sa = eventStartMs(a);
    const sb = eventStartMs(b);
    if (sa !== sb) return sa - sb;
    return a.addedAt - b.addedAt;
  });
}

/** True when the given event+date is already on the plan. */
export function isCalendarTodo(eventId: string, date: string): boolean {
  const id = keyFor(eventId, date);
  return readRaw().items.some((i) => i.id === id);
}

/**
 * Toggle an event onto / off the plan. Ticking an item that's already present
 * REMOVES it (the U6 contract: ticked = done = gone). Returns the new presence
 * state (true = now on the plan, false = removed).
 */
export function toggleCalendarTodo(input: {
  eventId: string;
  date: string;
  title: string;
  /** Floating wall-clock ISO start/end, so the plan can auto-drop past events. */
  start?: string;
  end?: string;
}): boolean {
  if (typeof input?.eventId !== "string" || input.eventId.length === 0) return false;
  const date = typeof input.date === "string" ? input.date : "";
  const id = keyFor(input.eventId, date);
  const shape = readRaw();
  const existing = shape.items.findIndex((i) => i.id === id);
  if (existing >= 0) {
    shape.items.splice(existing, 1);
    writeRaw(shape);
    emit();
    return false;
  }
  const item: CalendarTodoItem = {
    id,
    eventId: input.eventId,
    date,
    title: typeof input.title === "string" && input.title.length > 0 ? input.title : input.eventId,
    addedAt: Date.now(),
  };
  if (typeof input.start === "string" && input.start.length > 0) item.start = input.start;
  if (typeof input.end === "string" && input.end.length > 0) item.end = input.end;
  shape.items.push(item);
  writeRaw(shape);
  emit();
  return true;
}

/** Remove a single to-do (used by the explicit tick-off button). */
export function removeCalendarTodo(id: string): void {
  const shape = readRaw();
  const next = shape.items.filter((i) => i.id !== id);
  if (next.length === shape.items.length) return;
  writeRaw({ items: next });
  emit();
}

export function clearCalendarTodos(): void {
  writeRaw({ items: [] });
  emit();
}
